import React, { useState } from 'react';

const KitesurfingCalculator = () => {
  // State variables
  const [weight, setWeight] = useState('');
  const [experienceLevel, setExperienceLevel] = useState('intermediate');
  const [kiteType, setKiteType] = useState('inflatable');
  const [lullsWindSpeed, setLullsWindSpeed] = useState('');
  const [gustsWindSpeed, setGustsWindSpeed] = useState('');
  const [temperature, setTemperature] = useState('');
  const [pressure, setPressure] = useState('');
  const [humidity, setHumidity] = useState('');
  const [results, setResults] = useState(null);

  // Functions translated from Python to JavaScript

  const calculateAirDensity = (temperature, pressure, humidity) => {
    const R_specific = 287.058; // J/(kg·K)
    const standardAirDensity = 1.225; // kg/m^3 at sea level

    if (!temperature || !pressure || !humidity) {
      return standardAirDensity;
    } else {
      try {
        const T = parseFloat(temperature) + 273.15; // Convert Celsius to Kelvin
        const p = parseFloat(pressure) * 100; // Convert hPa to Pascals
        const e_s =
          6.1078 *
          10 ** ((7.5 * parseFloat(temperature)) / (237.3 + parseFloat(temperature))) *
          100; // Saturation vapor pressure in Pa
        const e = (parseFloat(humidity) / 100) * e_s; // Actual vapor pressure
        const p_dry = p - e; // Partial pressure of dry air
        const rho = p_dry / (R_specific * T); // Air density
        return rho;
      } catch (e) {
        console.error(`Error calculating air density: ${e}`);
        return standardAirDensity;
      }
    }
  };

  const calculateLift = (kiteSize, windSpeedKnots, airDensity, cl) => {
    const windSpeed = parseFloat(windSpeedKnots) * 0.514444; // Convert knots to m/s
    const liftForce =
      0.5 * airDensity * windSpeed ** 2 * kiteSize * cl; // Lift in Newtons
    const liftKg = liftForce / 9.81; // Convert Newtons to kg
    return liftKg;
  };

  const getPressureRange = (weight, experienceLevel) => {
    const pressureRanges75kg = {
      beginner: { min: 65, max: 170 },
      intermediate: { min: 65, max: 200 },
      advanced: { min: 65, max: 280 },
      expert: { min: 65, max: 400 },
    };

    let baseRange = pressureRanges75kg[experienceLevel.toLowerCase()];
    if (!baseRange) {
      console.log("Invalid experience level. Defaulting to 'intermediate'.");
      baseRange = pressureRanges75kg['intermediate'];
    }

    const weightDifference = parseFloat(weight) - 75; // kg
    const adjustedMin = baseRange.min + weightDifference;
    const adjustedMax = baseRange.max + weightDifference;

    return { adjustedMin, adjustedMax };
  };

  const getMinKiteSize = (weight, experienceLevel) => {
    const minKiteSizes = {
      expert: { [-Infinity]: 0 }, // No restriction
      advanced: { 70: 7, 60: 6, [-Infinity]: 5 },
      intermediate: { 70: 8, 60: 7, [-Infinity]: 6 },
      beginner: { 70: 9, 60: 8, [-Infinity]: 7 },
    };
    const experienceMinSizes =
      minKiteSizes[experienceLevel.toLowerCase()] || minKiteSizes['intermediate'];

    const weightLimits = Object.keys(experienceMinSizes)
      .map(Number)
      .sort((a, b) => b - a);

    for (let weightLimit of weightLimits) {
      if (parseFloat(weight) >= weightLimit) {
        return experienceMinSizes[weightLimit];
      }
    }
    return 0;
  };

  const getMaxKiteSize = (windSpeedKnots, experienceLevel) => {
    let maxKite;
    windSpeedKnots = parseFloat(windSpeedKnots);
    if (experienceLevel === 'beginner') {
      if (windSpeedKnots < 18) maxKite = 17;
      else if (windSpeedKnots < 20) maxKite = 15;
      else if (windSpeedKnots < 25) maxKite = 13;
      else if (windSpeedKnots < 28) maxKite = 11;
      else maxKite = 10;
    } else if (experienceLevel === 'intermediate') {
      if (windSpeedKnots < 18) maxKite = 17;
      else if (windSpeedKnots < 20) maxKite = 15;
      else if (windSpeedKnots < 25) maxKite = 13;
      else if (windSpeedKnots < 28) maxKite = 11;
      else maxKite = 10;
    } else if (
      experienceLevel === 'advanced' ||
      experienceLevel === 'expert'
    ) {
      if (windSpeedKnots < 20) maxKite = 18;
      else if (windSpeedKnots < 22) maxKite = 17;
      else if (windSpeedKnots < 25) maxKite = 15;
      else if (windSpeedKnots < 30) maxKite = 13;
      else if (windSpeedKnots < 33) maxKite = 11;
      else maxKite = 10;
    } else {
      maxKite = 15;
    }
    return maxKite;
  };

  const calculateKiteSize = () => {
    // Input validation
    if (!weight || isNaN(weight) || weight <= 0) {
      alert('Please enter a valid weight.');
      return;
    }
    if (!lullsWindSpeed || !gustsWindSpeed || isNaN(lullsWindSpeed) || isNaN(gustsWindSpeed)) {
      alert('Please enter valid wind speeds.');
      return;
    }

    const cl = kiteType === 'foil' ? 1.4 : 1.1;
    const airDensity = calculateAirDensity(temperature, pressure, humidity);
    const avgWindSpeed =
      (parseFloat(lullsWindSpeed) + parseFloat(gustsWindSpeed)) / 2;

    const { adjustedMin: minPressure, adjustedMax: maxPressure } = getPressureRange(
      weight,
      experienceLevel
    );

    const minKiteSize = getMinKiteSize(weight, experienceLevel);
    const maxKiteSize = getMaxKiteSize(gustsWindSpeed, experienceLevel);

    const kiteSizes = [];
    for (let size = 5; size <= 18; size++) {
      if (size >= minKiteSize && size <= maxKiteSize) {
        kiteSizes.push(size);
      }
    }

    const suitableKites = [];

    kiteSizes.forEach((kiteSize) => {
      const liftAvg = calculateLift(kiteSize, avgWindSpeed, airDensity, cl);
      const liftGust = calculateLift(kiteSize, gustsWindSpeed, airDensity, cl);

      if (liftAvg >= minPressure && liftGust <= maxPressure) {
        suitableKites.push(kiteSize);
      }
    });

    setResults({
      suitableKites,
      airDensity: airDensity.toFixed(3),
      avgWindSpeed: avgWindSpeed.toFixed(1),
      minPressure: minPressure.toFixed(1),
      maxPressure: maxPressure.toFixed(1),
    });
  };

  return (
    <div className="max-w-2xl mx-auto p-4 bg-white rounded shadow">
      <h1 className="text-2xl font-bold mb-4">
        Big Air Kitesurfing Kite Size Calculator
      </h1>
      <div className="space-y-4">
        <div>
          <label className="block font-medium">Weight (kg):</label>
          <input
            type="number"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            className="border p-2 w-full"
            placeholder="Enter your weight"
          />
        </div>
        <div>
          <label className="block font-medium">Experience Level:</label>
          <select
            value={experienceLevel}
            onChange={(e) => setExperienceLevel(e.target.value)}
            className="border p-2 w-full"
          >
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
            <option value="expert">Expert</option>
          </select>
        </div>
        <div>
          <label className="block font-medium">Kite Type:</label>
          <select
            value={kiteType}
            onChange={(e) => setKiteType(e.target.value)}
            className="border p-2 w-full"
          >
            <option value="inflatable">Inflatable</option>
            <option value="foil">Foil</option>
          </select>
        </div>
        <div>
          <label className="block font-medium">Wind Speed (Lulls) in knots:</label>
          <input
            type="number"
            value={lullsWindSpeed}
            onChange={(e) => setLullsWindSpeed(e.target.value)}
            className="border p-2 w-full"
            placeholder="Enter lulls wind speed"
          />
        </div>
        <div>
          <label className="block font-medium">Wind Speed (Gusts) in knots:</label>
          <input
            type="number"
            value={gustsWindSpeed}
            onChange={(e) => setGustsWindSpeed(e.target.value)}
            className="border p-2 w-full"
            placeholder="Enter gusts wind speed"
          />
        </div>
        <details>
          <summary className="cursor-pointer font-medium">
            Optional Atmospheric Inputs
          </summary>
          <div className="mt-2 space-y-2">
            <div>
              <label className="block font-medium">Temperature (°C):</label>
              <input
                type="number"
                value={temperature}
                onChange={(e) => setTemperature(e.target.value)}
                className="border p-2 w-full"
                placeholder="Optional"
              />
            </div>
            <div>
              <label className="block font-medium">Pressure (hPa):</label>
              <input
                type="number"
                value={pressure}
                onChange={(e) => setPressure(e.target.value)}
                className="border p-2 w-full"
                placeholder="Optional"
              />
            </div>
            <div>
              <label className="block font-medium">Humidity (%):</label>
              <input
                type="number"
                value={humidity}
                onChange={(e) => setHumidity(e.target.value)}
                className="border p-2 w-full"
                placeholder="Optional"
              />
            </div>
          </div>
        </details>
        <button
          onClick={calculateKiteSize}
          className="bg-blue-600 text-white py-2 px-4 rounded"
        >
          Calculate Kite Size
        </button>
      </div>
      {results && (
        <div className="mt-6">
          <h2 className="text-xl font-bold">Results:</h2>
          {results.suitableKites.length > 0 ? (
            <div className="mt-2">
              <p>
                <strong>Suitable Kite Sizes:</strong>{' '}
                {results.suitableKites.join(', ')} m²
              </p>
              <p>
                <strong>Average Wind Speed:</strong> {results.avgWindSpeed} knots
              </p>
              <p>
                <strong>Air Density:</strong> {results.airDensity} kg/m³
              </p>
              <p>
                <strong>Acceptable Pressure Range:</strong> {results.minPressure} kg
                - {results.maxPressure} kg
              </p>
            </div>
          ) : (
            <div className="mt-2 text-red-600">
              <p>No suitable kite sizes found for the given conditions.</p>
              <p>
                Conditions may be too light or too strong for your level and
                weight.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default KitesurfingCalculator;
